import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtDate, fmtMonth } from "@/lib/format";

const f0 = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const f2 = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const R = (v) => `${v < 0 ? "-" : ""}Rs ${f0.format(Math.abs(Math.round(v || 0)))}`;
const R2 = (v) => `${v < 0 ? "-" : ""}Rs ${f2.format(Math.abs(v || 0))}`;
const AMBER = [217, 119, 6], DARK = [30, 41, 59];
const head = { fillColor: DARK, textColor: 255, fontStyle: "bold", fontSize: 8.5 };
const foot = { fillColor: [254, 243, 199], textColor: [120, 53, 15], fontStyle: "bold", fontSize: 8.5 };
const last = (doc) => doc.lastAutoTable.finalY;

function title(doc, text, y) {
  doc.setFontSize(11); doc.setTextColor(...DARK); doc.setFont("helvetica", "bold");
  doc.text(text, 40, y);
  return y + 10;
}

function banner(doc, business, line1, line2) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...AMBER); doc.rect(0, 0, w, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...DARK);
  doc.text(business || "MANJAP HOSPITALITY SOLUTIONS", 40, 36);
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text(line1, 40, 56);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100);
  doc.text(line2, 40, 72);
  doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, w - 40, 72, { align: "right" });
}

function pageFooter(doc) {
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth(), h = doc.internal.pageSize.getHeight();
    doc.setFontSize(7); doc.setTextColor(140);
    doc.text("Manjap Staff Salary  -  Good Food. Great Mood.", 40, h - 18);
    doc.text(`Page ${i} of ${n}`, w - 40, h - 18, { align: "right" });
  }
}

const period = (month) => {
  const d = new Date(`${month}-01T00:00:00`);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const o = { day: "numeric", month: "short", year: "numeric" };
  return `Period: ${d.toLocaleDateString("en-IN", o)} - ${end.toLocaleDateString("en-IN", o)}`;
};

export function exportSalarySlipPdf(row, month, business) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  banner(doc, business, `${row.name} - Salary & Due Summary`, period(month));
  doc.setTextColor(60); doc.setFontSize(9);
  doc.text(`Fixed Monthly Salary: ${R2(row.salary)}${row.role ? `   |   ${row.role}` : ""}`, 40, 88);
  doc.text(`Leave Taken: ${row.leave_days} day${row.leave_days === 1 ? "" : "s"}`, 40, 102);

  let y = title(doc, "Salary Calculation", 128);
  const body = [
    ["Fixed Salary", R2(row.salary)],
    [`Daily Rate (${f0.format(row.salary)} / ${row.days_in_month})`, R2(row.daily_rate)],
    [`Less: ${row.leave_days} Day Leave`, `- ${R2(row.leave_deduction)}`],
    ["Net Salary (this month)", R2(row.net_salary)],
  ];
  if (row.previous_balance) body.push([row.previous_balance > 0 ? "Add: Previous Balance (pending)" : "Less: Previous Advance / Excess paid", R2(row.previous_balance)]);
  body.push(["Total Salary Payable", R2(row.payable)]);
  autoTable(doc, {
    startY: y, theme: "grid", headStyles: head, styles: { fontSize: 9.5, cellPadding: 5 }, margin: { left: 40, right: 40 },
    columnStyles: { 1: { halign: "right" } },
    head: [["Description", "Amount"]], body,
    didParseCell: (d) => {
      if (d.section === "body" && (d.row.index === 3 || d.row.index === body.length - 1)) {
        d.cell.styles.fontStyle = "bold"; d.cell.styles.fillColor = [254, 243, 199];
      }
    },
  });

  y = title(doc, "Payments / Advances given", last(doc) + 26);
  autoTable(doc, {
    startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 9, cellPadding: 4 }, margin: { left: 40, right: 40 },
    columnStyles: { 2: { halign: "right" } },
    head: [["Date", "Item", "Amount", "Paid by", "Note"]],
    body: row.entries.length
      ? row.entries.map((e) => [fmtDate(e.date, { day: "numeric", month: "short" }), e.item, R2(e.amount), e.payer || "—", e.note || ""])
      : [["-", "No payments recorded", R2(0), "", ""]],
    foot: [["", "TOTAL PAID", R2(row.paid), "", ""]],
  });

  y = title(doc, "Final Settlement", last(doc) + 26);
  const dueLabel = row.balance >= 0 ? `Balance Payable to ${row.name}` : `Balance Due from ${row.name}`;
  autoTable(doc, {
    startY: y, theme: "grid", headStyles: head, styles: { fontSize: 10, cellPadding: 6 }, margin: { left: 40, right: 40 },
    tableWidth: 330, columnStyles: { 1: { halign: "right" } },
    head: [["Description", "Amount"]],
    body: [
      ["Net Salary (after leave deduction)", R2(row.net_salary)],
      ["Previous Balance", R2(row.previous_balance)],
      ["Total Already Paid", R2(row.paid)],
      [dueLabel, R2(Math.abs(row.balance))],
    ],
    didParseCell: (d) => { if (d.section === "body" && d.row.index === 3) { d.cell.styles.fontStyle = "bold"; d.cell.styles.fillColor = [254, 243, 199]; } },
  });

  y = last(doc) + 56;
  doc.setFontSize(9); doc.setTextColor(80); doc.setFont("helvetica", "normal");
  doc.text(`Received by: ____________________  (${row.name})`, 40, y);
  doc.text("Paid by: ____________________", 40, y + 28);
  pageFooter(doc);
  doc.save(`${row.name.replace(/\s+/g, "_")}_Salary_${month}.pdf`);
}

export function exportSalarySummaryPdf(data) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const rows = data.staff.filter((r) => r.active || r.paid || r.balance);
  const t = data.totals;
  banner(doc, data.business_name, `Staff Salary Summary - ${fmtMonth(data.month)}`, period(data.month));

  let y = title(doc, `All staff (${rows.length})`, 100);
  autoTable(doc, {
    startY: y, theme: "striped", headStyles: head, footStyles: foot, styles: { fontSize: 8.5, cellPadding: 4 }, margin: { left: 40, right: 40 },
    columnStyles: Object.fromEntries([2, 3, 4, 5, 6, 7, 8].map((i) => [i, { halign: "right" }])),
    head: [["Staff", "Role", "Monthly Salary", "Leave", "Leave Cut", "Net Salary", "Prev. Balance", "Paid", "Balance"]],
    body: rows.map((r) => [r.name, r.role || "-", R(r.salary), String(r.leave_days), R(r.leave_deduction), R(r.net_salary), R(r.previous_balance), R(r.paid), R(r.balance)]),
    foot: [["TOTAL", "", R(t.salary), String(t.leave_days), "", R(t.net_salary), R(t.previous_balance), R(t.paid), R(t.balance)]],
  });

  rows.filter((r) => r.entries.length).forEach((r) => {
    y = title(doc, `${r.name} - date-wise payments (paid ${R(r.paid)}, balance ${R(r.balance)})`, last(doc) + 24);
    if (y > doc.internal.pageSize.getHeight() - 140) { doc.addPage(); banner(doc, data.business_name, `Staff Salary Summary - ${fmtMonth(data.month)}`, period(data.month)); y = title(doc, `${r.name} - date-wise payments`, 100); }
    autoTable(doc, {
      startY: y, theme: "grid", headStyles: head, footStyles: foot, styles: { fontSize: 8, cellPadding: 3 }, margin: { left: 40, right: 40 },
      tableWidth: 560, columnStyles: { 2: { halign: "right" } },
      head: [["Date", "Item", "Amount", "Paid by", "Note"]],
      body: r.entries.map((e) => [fmtDate(e.date, { day: "numeric", month: "short" }), e.item, R2(e.amount), e.payer || "—", e.note || ""]),
      foot: [["", "TOTAL PAID", R2(r.paid), "", ""]],
    });
  });

  pageFooter(doc);
  doc.save(`Manjap_Staff_Salary_${data.month}.pdf`);
}
